import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  getTestimonials,
  saveTestimonial,
  updateTestimonial,
  deleteTestimonial,
} from "../Apis/TestimonialAPIs";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import { formatDate } from "../helper/DateHelper";
import TablePagination from "./TablePagination";

const extractYouTubeId = (url) => {
  if (!url) return null;
  const trimmed = url.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return trimmed;
  }
  const regExp = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/;
  const match = trimmed.match(regExp);
  return match ? match[1] : null;
};

const TestimonialLayer = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");

  const previewId = extractYouTubeId(videoUrl);

  const fetchTestimonialsList = async () => {
    setLoading(true);
    const res = await getTestimonials({
      page: currentPage,
      limit: rowsPerPage,
    });
    if (res.status) {
      const data = res.response?.data || res.response || [];
      setTestimonials(Array.isArray(data) ? data : []);
      setTotalRecords(
        res.response?.total ?? (Array.isArray(data) ? data.length : 0)
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTestimonialsList();
  }, [currentPage, rowsPerPage]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      setError("YouTube URL is Required");
      return;
    }

    const videoId = extractYouTubeId(videoUrl);
    if (!videoId) {
      setError("Please enter a valid YouTube URL (Shorts, Watch, or youtu.be link)");
      return;
    }

    if (editId) {
      const res = await updateTestimonial(editId, {
        videoUrl: videoUrl.trim(),
      });
      if (res.status) {
        setVideoUrl("");
        setEditId(null);
        setError("");
        fetchTestimonialsList();
      }
    } else {
      const res = await saveTestimonial({
        videoUrl: videoUrl.trim(),
      });
      if (res.status) {
        toast.success("Testimonial Added Successfully");
        setVideoUrl("");
        setError("");
        fetchTestimonialsList();
      }
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id || item.id);
    setVideoUrl(item.videoUrl || `https://youtube.com/shorts/${item.videoId}`);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setVideoUrl("");
    setError("");
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsUpdating(true);
    const res = await deleteTestimonial(itemToDelete._id || itemToDelete.id);
    if (res.status) {
      toast.success("Testimonial Deleted Successfully");
      fetchTestimonialsList();
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
    setIsUpdating(false);
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background:
            "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0">Testimonials</h6>
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit} className="row g-3 align-items-start mb-40">
          <div className="col-md-8">
            <label className="form-label fw-bold">
              YouTube URL <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control radius-8 ${error ? "is-invalid" : ""}`}
              placeholder="e.g. https://youtube.com/shorts/P5TrAdK8x8Y"
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value);
                if (error) setError("");
              }}
            />
            {error && <div className="invalid-feedback">{error}</div>}

            {/* Live Preview Box */}
            {previewId && (
              <div className="d-flex align-items-center gap-3 p-12 radius-8 border bg-neutral-50 mt-3" style={{ maxWidth: "420px" }}>
                <img
                  src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`}
                  alt="YouTube Preview"
                  style={{ width: "90px", height: "60px", objectFit: "cover", borderRadius: "6px" }}
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${previewId}/default.jpg`;
                  }}
                />
                <div>
                  <span className="badge bg-success-focus text-success-600 px-8 py-2 radius-4 text-xs fw-bold mb-1">
                    Valid Video Link
                  </span>
                  <div className="text-dark fw-semibold text-sm text-truncate" style={{ maxWidth: "280px" }}>
                    ID: {previewId}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-md-4 d-flex gap-2">
            <button
              type="submit"
              className="btn radius-8 w-100 py-11 fw-bold mt-32 d-flex align-items-center justify-content-center gap-2"
              style={{
                backgroundColor: "#f7e7da",
                color: "black",
                fontWeight: "600",
                border: "none",
              }}
            >
              {!editId && <Icon icon="ic:baseline-plus" className="text-xl" />}
              {editId ? "Update Testimonial" : "Add Testimonial"}
            </button>
            {editId && (
              <button
                type="button"
                className="btn btn-outline-secondary radius-8 py-11 fw-bold mt-32"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold", width: "80px" }}>
                  S.No
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold", width: "120px" }}>
                  Thumbnail
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  YouTube Link
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold", width: "160px" }}>
                  Created Date
                </th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ color: "black", fontWeight: "bold", width: "120px" }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-24">
                    Loading...
                  </td>
                </tr>
              ) : testimonials.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-24">
                    No testimonials found
                  </td>
                </tr>
              ) : (
                testimonials.map((item, index) => (
                  <tr key={item._id || item.id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td>
                      <a
                        href={item.videoUrl || `https://youtube.com/shorts/${item.videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-block"
                        title="Click to watch on YouTube"
                      >
                        <img
                          src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                          alt="Thumbnail"
                          style={{
                            width: "70px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #e0e0e0",
                          }}
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${item.videoId}/default.jpg`;
                          }}
                        />
                      </a>
                    </td>
                    <td>
                      <a
                        href={item.videoUrl || `https://youtube.com/shorts/${item.videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary fw-semibold text-decoration-underline d-inline-flex align-items-center gap-1"
                        style={{ wordBreak: "break-all" }}
                      >
                        <span>{item.videoUrl || `https://youtube.com/shorts/${item.videoId}`}</span>
                        <Icon icon="lucide:external-link" className="text-xs" />
                      </a>
                    </td>
                    <td className="text-secondary-light">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Edit"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          title="Delete"
                          className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                        >
                          <Icon icon="lucide:trash-2" className="text-xl" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon
                icon="solar:trash-bin-trash-bold"
                className="text-danger-600 text-xxl"
              />
            </div>
          </div>
          <h5 className="mb-2">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete this testimonial?
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              variant="outline-secondary"
              className="px-32 radius-8"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{
                backgroundColor: "#ed1c24",
                color: "white",
              }}
            >
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TestimonialLayer;
